// src/components/SimpleFormComponents/CardHeader.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Card, Col, Flex, Row, Grid, Divider, theme, Collapse } from 'antd';
import useZustand from 'hooks/useZustand';
import utils from 'utils/utils';
import CardHeaderAvatar from 'layouts/Pages/PageHeader/CardHeaderAvatar';
import ActionsButton from 'layouts/Pages/PageHeader/ActionsButton';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleSegment from 'components/SimpleComponents/SimpleSegment';
import { SettingFilled } from '@ant-design/icons';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CardHeader = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // overlapsBackground: when true, card has negative margin to overlap with external background
  const { data, avatarName, countryCode, overlapsBackground = false } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentPage = useZustand('module', 'currentPage');

  // Get locked from SimpleFormContext (may be undefined if not inside SimpleFormProvider)
  const formContext = useSimpleFormContext();
  const locked = formContext?.locked;
  const form = formContext?.form;

  const { tab } = useDocumentContext();
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const showAvatar = storedCurrentPage?.header?.avatar?.visible;
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('sm');

  // components
  const cardComponents = storedCurrentPage?.header?.card;
  const cardTabComponents = storedCurrentPage?.tabs?.[tab]?.card;

  // Build props object with data spread and form for card components
  const cardProps = { ...data, form };

  const title = cardComponents?.title?.(cardProps) || cardTabComponents?.title?.(cardProps) || false;
  const subtitle = cardComponents?.subtitle?.(cardProps) || cardTabComponents?.subtitle?.(cardProps) || false;
  const componentColumn1 =
    cardComponents?.column1?.(cardProps) || cardTabComponents?.column1?.(cardProps) || false;
  const componentColumn2 =
    cardComponents?.column2?.(cardProps) || cardTabComponents?.column2?.(cardProps) || false;
  const componentColumn3 =
    cardComponents?.column3?.(cardProps) || cardTabComponents?.column3?.(cardProps) || false;
  const componentTags = cardComponents?.tags?.(cardProps) || cardTabComponents?.tags?.(cardProps) || false;

  // Pass countryCode to icons function if provided
  const componentIcons =
    cardComponents?.icons?.(cardProps, countryCode) ||
    cardTabComponents?.icons?.(cardProps, countryCode) ||
    false;

  const componentBottom =
    cardComponents?.bottom?.(cardProps) || cardTabComponents?.bottom?.(cardProps) || false;

  const avatarNamePrecedence =
    avatarName || cardComponents?.avatarName?.(cardProps) || cardTabComponents?.avatarName?.(cardProps) || '';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Calculate card marginTop:
  // - overlapsBackground: negative margin to overlap with external background (PageHeader)
  // - showAvatar: positive margin to make room for avatar that extends above card
  // - default: no margin
  // const cardMarginTop = overlapsBackground ? (isMobile ? -48 : -94) : showAvatar ? 42 : 0;
  const cardMarginTop = 0;

  return (
    <>
      {/* WRAPPER - for positioning loading area behind avatar */}
      <div style={{ position: 'relative' }}>
        {/* AVATAR - positioned outside Card stacking context */}
        {showAvatar ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: isMobile ? '50%' : 24,
              transform: isMobile ? 'translateX(-50%)' : 'none',
              zIndex: 101,
            }}
          >
            <CardHeaderAvatar
              {...props}
              lockAvatar={locked}
              record={data}
              avatarNamePrecedence={avatarNamePrecedence}
            />
          </div>
        ) : null}

        <Card
          className="with-shadow"
          style={{
            marginTop: cardMarginTop,
            position: 'relative',
            minHeight: showAvatar ? 160 : undefined,
          }}
        >
          <Row wrap={isMobile} gutter={[8, 8]} align="top">
            {/* AVATAR SPACER - reserve space for the absolutely positioned avatar */}
            {showAvatar ? (
              <Col
                flex={isMobile ? '100%' : 'none'}
                className="mr-3"
                style={{
                  display: 'flex',
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  width: isMobile ? '100%' : 'auto',
                  // Invisible spacer to maintain layout
                  visibility: 'hidden',
                }}
              >
                <div style={{ width: 158, height: isMobile ? 158 : 'auto' }} />
              </Col>
            ) : null}
            {/* AVATAR SPACER */}

            {/* CONTENT */}
            <Col
              flex={isMobile ? '100%' : '1 1 auto'}
              style={{
                minWidth: isMobile ? '100%' : 0,
              }}
            >
              {/* TITLE, SUBTITLE AND ACTIONS BUTTONS */}
              <Row
                wrap={isMobile}
                justify={isMobile ? 'center' : 'space-between'}
                align="top"
                style={{ width: '100%' }}
              >
                {/* TITLE AND SUBTITLE */}
                <Col
                  style={{
                    flex: isMobile ? '0 0 100%' : '1 1 auto',
                    minWidth: isMobile ? '100%' : 0,
                    width: isMobile ? '100%' : 'auto',
                    textAlign: isMobile ? 'center' : 'left',
                    overflow: 'hidden',
                  }}
                >
                  {/* TITLE */}
                  <div
                    style={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: isMobile ? 'center' : 'left',
                      // height: 41,
                    }}
                  >
                    <h2
                      // className="mb-1"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginBottom: 0,
                      }}
                    >
                      {title}
                    </h2>
                  </div>

                  {/* SUBTITLE */}
                  <div
                    style={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: isMobile ? 'center' : 'left',
                    }}
                  >
                    <div
                      // className="mb-3"
                      className="text-muted"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {subtitle}
                    </div>
                  </div>
                </Col>
                {/* TITLE AND SUBTITLE */}

                {/* ACTIONS BUTTONS */}
                <Col
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: isMobile ? '100%' : 'auto',
                    width: isMobile ? '100%' : 'auto',
                    display: 'flex',
                    justifyContent: isMobile ? 'center' : 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <ActionsButton
                    {...props}
                    affixWhen="always"
                    backgroundColor={token.colorBgContainer}
                    locate="pageSettings"
                  />
                </Col>
                {/* ACTIONS BUTTONS */}
              </Row>
              {/* TITLE, SUBTITLE AND ACTIONS BUTTONS */}

              {/* COLUMNS */}
              <Row justify="space-between" wrap={true} className="mt-3">
                <Col xs={24} md={8}>
                  {componentColumn1 || <span />}
                </Col>

                <Col xs={24} md={8}>
                  {componentColumn2 || <span />}
                </Col>

                <Col xs={24} md={8}>
                  {componentColumn3 || <span />}
                </Col>
              </Row>
              {/* COLUMNS */}

              {/*<Row justify="end">*/}
              {componentTags || componentIcons ? (
                <Flex justify="flex-end" align="center" className="mt-3">
                  {componentTags ? componentTags : <span></span>}
                  {componentTags && componentIcons ? <Divider orientation="vertical" /> : null}
                  {componentIcons ? componentIcons : <span></span>}
                </Flex>
              ) : null}
              {/*</Row>*/}
            </Col>
            {/* CONTENT */}
          </Row>

          {/*          <Collapse
            ghost
            size="small"
            style={{ marginTop: 8 }}
            items={[
              {
                key: 'moreInformation',
                label: (
                  <span>
                    <SettingFilled style={{ marginRight: 6 }} />
                    {translateX('more_information')}
                  </span>
                ),
                children: <>DATA</>,
              },
            ]}
          />*/}
        </Card>
      </div>

      {componentBottom ? (
        <Card>
          <Row className="w-100 mt-3">
            <Col
              style={{
                flex: '0 0 100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {componentBottom}
            </Col>
          </Row>
        </Card>
      ) : null}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CardHeader;
